import React, { useState, useEffect, useContext, useRef } from 'react';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import OptionRowFloat from 'components/layout_and_quote_builder/options/OptionRowFloat.jsx'
import LoadFromLocalStorage from 'components/layout_and_quote_builder/options/LoadFromLocalStorage.jsx'
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import Constants from 'components/layout_and_quote_builder/Constants.jsx'
import MyEventListener from 'components/common/MyEventListener.jsx'

const MAXIMUM_LAYOUT_DIMENSIONS = 3;

// Layout dimensions to hide from the dropdown
const HIDDEN_LAYOUT_DIMENSIONS = [
  "2A0 1189mm X 1682mm",
  "4A0 1682mm X 2378mm",
];

// For "Other sizes" group, only show these dimensions
const ALLOWED_OTHER_SIZES = [
  "700mm x 1000mm",
];

const ChooseLayoutDimensions = ({ index, title, item, hideMargins, hideCustom, publicSetter, defaultValues, smallerSubSetOf, disableMargins}) => {
  const myLayoutContext = useContext(LayoutContext);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Convert single ID to array for backwards compatibility with defaultValues
  const getInitialLayoutDimensionIds = () => {
    if (defaultValues["layout_dimension_ids"]) {
      return Array.isArray(defaultValues["layout_dimension_ids"])
        ? defaultValues["layout_dimension_ids"]
        : [defaultValues["layout_dimension_ids"]];
    }
    if (defaultValues["layout_dimension_id"]) {
      return [defaultValues["layout_dimension_id"]];
    }
    if (myLayoutContext.layoutOptions.layout_dimension_ids) {
      return myLayoutContext.layoutOptions.layout_dimension_ids;
    }
    if (myLayoutContext.layoutOptions.layout_dimension_id) {
      return [myLayoutContext.layoutOptions.layout_dimension_id];
    }
    return [21]; // Default fallback
  };

  const [layout_dimension_ids, set_layout_dimension_ids] = useState(getInitialLayoutDimensionIds());
  const [layout_dimension_width, set_layout_dimension_width] = useState( defaultValues["layout_dimension_width"] || myLayoutContext.layoutOptions.layout_dimension_width);
  const [layout_dimension_height, set_layout_dimension_height] = useState(defaultValues["layout_dimension_height"] || myLayoutContext.layoutOptions.layout_dimension_height);

  const [layout_dimension_top_margin_diecutting, set_layout_dimension_top_margin_diecutting]       = useState(defaultValues["layout_dimension_top_margin"]    || myLayoutContext.layoutOptions.layout_dimension_top_margin);
  const [layout_dimension_side_margin_diecutting, set_layout_dimension_side_margin_diecutting]     = useState(defaultValues["layout_dimension_side_margin"]   || myLayoutContext.layoutOptions.layout_dimension_side_margin);
  const [layout_dimension_bottom_margin_diecutting, set_layout_dimension_bottom_margin_diecutting] = useState(defaultValues["layout_dimension_bottom_margin"] || myLayoutContext.layoutOptions.layout_dimension_bottom_margin);

  const [layout_dimension_top_margin_printing, set_layout_dimension_top_margin_printing]       = useState(defaultValues["layout_dimension_top_margin"]    || myLayoutContext.layoutOptions.layout_dimension_top_margin);
  const [layout_dimension_side_margin_printing, set_layout_dimension_side_margin_printing]     = useState(defaultValues["layout_dimension_side_margin"]   || myLayoutContext.layoutOptions.layout_dimension_side_margin);
  const [layout_dimension_bottom_margin_printing, set_layout_dimension_bottom_margin_printing] = useState(defaultValues["layout_dimension_bottom_margin"] || myLayoutContext.layoutOptions.layout_dimension_bottom_margin);

  const [layout_dimension_filtering_enabled, set_layout_dimension_filtering_enabled] = useState( false );

  // Temporary display state for immediate UI feedback
  const [temp_top_margin_diecutting, set_temp_top_margin_diecutting] = useState(layout_dimension_top_margin_diecutting);
  const [temp_side_margin_diecutting, set_temp_side_margin_diecutting] = useState(layout_dimension_side_margin_diecutting);
  const [temp_bottom_margin_diecutting, set_temp_bottom_margin_diecutting] = useState(layout_dimension_bottom_margin_diecutting);
  const [temp_top_margin_printing, set_temp_top_margin_printing] = useState(layout_dimension_top_margin_printing);
  const [temp_side_margin_printing, set_temp_side_margin_printing] = useState(layout_dimension_side_margin_printing);
  const [temp_bottom_margin_printing, set_temp_bottom_margin_printing] = useState(layout_dimension_bottom_margin_printing);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSmallerSubSetOfValues = () => {
    if(smallerSubSetOf == undefined) { return [ ] }
    const res = [];
    var width = 0
    var height = 0

    for (let [key, values] of Object.entries(myLayoutContext.dimensionGroups)) {
      for(let i=0; i < values.length; i++) {

        if(values[i].id == smallerSubSetOf) {
          width = values[i].width
          height = values[i].height
        }
      }
    }

    return [width, height]
  }

  const correct = (value) => {
    var close = 0.05;
    if( Math.abs( Math.ceil(value) - value ) < close ) { return true }
    if( Math.abs( Math.floor(value) - value ) < close ) { return true }

    return false
  }

  const getSmallerSubSetOfIds = () => {
    if(smallerSubSetOf == undefined) {
      return []
    }

    let smallerSubSetOfValues = getSmallerSubSetOfValues()
    const w = smallerSubSetOfValues[0];
    const h = smallerSubSetOfValues[1];
    var res = []

    for (let [key, values] of Object.entries(myLayoutContext.dimensionGroups)) {
      for(let i=0; i < values.length; i++) {
        let value = values[i];
        let w_part = w / value.width;
        let h_part = h / value.height;
        if( w_part > 0.99 && h_part > 0.99 && correct(w_part) && correct(h_part) ) {
          res.push(value.id)
        }
      }
    }
    return res
  }

  // Check if a layout dimension should be shown based on filtering rules
  const shouldShowLayoutDimension = (value, groupName) => {
    // Hide specific dimensions by description
    if (HIDDEN_LAYOUT_DIMENSIONS.includes(value.description)) {
      return false;
    }

    // For "Other sizes" group, only show allowed dimensions
    if (groupName === "Other sizes") {
      return ALLOWED_OTHER_SIZES.includes(value.description);
    }

    return true;
  };

  // Get all layout dimension options as a flat array
  const getAllLayoutDimensionOptions = () => {
    let options = [];
    let smallerSubSetOfListIds = getSmallerSubSetOfIds();

    for (let [groupName, values] of Object.entries(myLayoutContext.dimensionGroups)) {
      let sortedValues = [...values].sort((a, b) => a.description.localeCompare(b.description));
      if (groupName === "Other sizes") {
        sortedValues = sortedValues.reverse();
      }

      sortedValues.forEach((value) => {
        // Apply visibility filtering
        if (!shouldShowLayoutDimension(value, groupName)) {
          return;
        }

        if (smallerSubSetOfListIds.length > 0) {
          if (smallerSubSetOfListIds.includes(value.id)) {
            options.push({ ...value, groupName });
          }
        } else {
          options.push({ ...value, groupName });
        }
      });
    }

    return options;
  };

  // Get description for a layout dimension ID
  const getDescriptionForId = (id) => {
    for (let [key, values] of Object.entries(myLayoutContext.dimensionGroups)) {
      for (let value of values) {
        if (value.id == id) {
          return value.description;
        }
      }
    }
    return `ID: ${id}`;
  };

  // Handle checkbox change for multi-select
  const handleCheckboxChange = (id) => {
    const numericId = parseInt(id, 10);
    set_layout_dimension_ids((prevIds) => {
      if (prevIds.includes(numericId)) {
        // Remove if already selected (but don't allow empty)
        const newIds = prevIds.filter(existingId => existingId !== numericId);
        return newIds.length > 0 ? newIds : prevIds;
      } else {
        // Add if not at max
        if (prevIds.length < MAXIMUM_LAYOUT_DIMENSIONS) {
          return [...prevIds, numericId];
        }
        return prevIds;
      }
    });
  };

  const formatValue = (value) => {
    let toFormat = parseFloat(value);
    let formatted = (Number.isNaN(toFormat) ? 0 : toFormat).toFixed(layout_dimension_unit == 'in' ? 4 : 2);
    return formatted
  }

  // Simple onChange handlers that update temp state immediately
  const handleTopMarginDiecutting = (value) => set_temp_top_margin_diecutting(value);
  const handleSideMarginDiecutting = (value) => set_temp_side_margin_diecutting(value);
  const handleBottomMarginDiecutting = (value) => set_temp_bottom_margin_diecutting(value);
  const handleTopMarginPrinting = (value) => set_temp_top_margin_printing(value);
  const handleSideMarginPrinting = (value) => set_temp_side_margin_printing(value);
  const handleBottomMarginPrinting = (value) => set_temp_bottom_margin_printing(value);

  // Edit/Save handler
  const handleEditSave = (e) => {
    e.preventDefault();
    if (isEditMode) {
      // Save mode: Update all permanent states
      set_layout_dimension_top_margin_diecutting(temp_top_margin_diecutting);
      set_layout_dimension_side_margin_diecutting(temp_side_margin_diecutting);
      set_layout_dimension_bottom_margin_diecutting(temp_bottom_margin_diecutting);
      set_layout_dimension_top_margin_printing(temp_top_margin_printing);
      set_layout_dimension_side_margin_printing(temp_side_margin_printing);
      set_layout_dimension_bottom_margin_printing(temp_bottom_margin_printing);
      setIsEditMode(false);
    } else {
      // Edit mode: Enable fields
      setIsEditMode(true);
    }
  };

  const onSolutionSet = (msg) => {
    if(msg.detail.index != index) { return }

    let diecutting_result = msg.detail.solution.diecutting_result
    let printing_result = diecutting_result.printing_result

    console.log( "-------------onSolutionSet------------------------" );

    // Exit edit mode since backend is providing new values
    setIsEditMode(false);

    const topDiecutting = formatValue(diecutting_result.user_margin_top);
    const sideDiecutting = formatValue(diecutting_result.user_margin_side);
    const bottomDiecutting = formatValue(diecutting_result.user_margin_bottom);
    const topPrinting = formatValue(printing_result.margins.user_margin_top);
    const sidePrinting = formatValue(printing_result.margins.user_margin_side);
    const bottomPrinting = formatValue(printing_result.margins.user_margin_bottom);

    // Update both permanent and temp states
    set_layout_dimension_top_margin_diecutting(topDiecutting);
    set_layout_dimension_side_margin_diecutting(sideDiecutting);
    set_layout_dimension_bottom_margin_diecutting(bottomDiecutting);
    set_layout_dimension_top_margin_printing(topPrinting);
    set_layout_dimension_side_margin_printing(sidePrinting);
    set_layout_dimension_bottom_margin_printing(bottomPrinting);

    set_temp_top_margin_diecutting(topDiecutting);
    set_temp_side_margin_diecutting(sideDiecutting);
    set_temp_bottom_margin_diecutting(bottomDiecutting);
    set_temp_top_margin_printing(topPrinting);
    set_temp_side_margin_printing(sidePrinting);
    set_temp_bottom_margin_printing(bottomPrinting);
  }

  const layout_dimension_unit = myLayoutContext.layoutOptions.layout_dimension_unit;
  const getPublicData = () => {
    return {
      layout_dimension_ids,
      layout_dimension_unit,
      layout_dimension_top_margin_diecutting,
      layout_dimension_side_margin_diecutting,
      layout_dimension_bottom_margin_diecutting,
      layout_dimension_top_margin_printing,
      layout_dimension_side_margin_printing,
      layout_dimension_bottom_margin_printing,
      layout_dimension_filtering_enabled,
    }
  }

  const setPublicData = () => { publicSetter( () => getPublicData() ) }
  const dimensionIdsSetter = (value) => {
    if (Array.isArray(value)) {
      set_layout_dimension_ids(value.map(v => parseInt(v, 10)).filter(v => v > 0));
    } else if (value > 0) {
      set_layout_dimension_ids([parseInt(value, 10)]);
    }
  }

  // Determine if fields should be disabled
  const fieldsDisabled = !isEditMode || disableMargins;

  useEffect(() => LoadFromLocalStorage(item, 'layout_dimension_ids', dimensionIdsSetter), []);

  if(publicSetter != undefined) {
    useEffect( setPublicData, [
      layout_dimension_ids,
      layout_dimension_unit,
      layout_dimension_top_margin_diecutting,
      layout_dimension_side_margin_diecutting,
      layout_dimension_bottom_margin_diecutting,
      layout_dimension_top_margin_printing,
      layout_dimension_side_margin_printing,
      layout_dimension_bottom_margin_printing,
      layout_dimension_filtering_enabled,
    ])
  }

  useEffect( () => {
    emitEvent(Constants.layout_dimension_changed, {
      index: index,
    })
  }, [layout_dimension_ids])

  // Sync temporary state with permanent state when permanent state changes externally
  useEffect(() => { set_temp_top_margin_diecutting(layout_dimension_top_margin_diecutting); }, [layout_dimension_top_margin_diecutting]);
  useEffect(() => { set_temp_side_margin_diecutting(layout_dimension_side_margin_diecutting); }, [layout_dimension_side_margin_diecutting]);
  useEffect(() => { set_temp_bottom_margin_diecutting(layout_dimension_bottom_margin_diecutting); }, [layout_dimension_bottom_margin_diecutting]);
  useEffect(() => { set_temp_top_margin_printing(layout_dimension_top_margin_printing); }, [layout_dimension_top_margin_printing]);
  useEffect(() => { set_temp_side_margin_printing(layout_dimension_side_margin_printing); }, [layout_dimension_side_margin_printing]);
  useEffect(() => { set_temp_bottom_margin_printing(layout_dimension_bottom_margin_printing); }, [layout_dimension_bottom_margin_printing]);

  MyEventListener( [{
    key: 'quote_builder.solution_set',
    callback: onSolutionSet
  }] )

  const multiSelect = () => {
    const allOptions = getAllLayoutDimensionOptions();
    const selectedDescriptions = layout_dimension_ids.map(id => getDescriptionForId(id));
    const displayText = selectedDescriptions.length > 0
      ? selectedDescriptions.join(', ')
      : 'Select dimensions...';

    // Group options by groupName
    const groupedOptions = {};
    allOptions.forEach(option => {
      if (!groupedOptions[option.groupName]) {
        groupedOptions[option.groupName] = [];
      }
      groupedOptions[option.groupName].push(option);
    });

    return (
      <table className="table" style={{marginBottom: '0'}}>
        <tbody>
          <tr>
            <td> {title != undefined ? title : window.wording_layouts_choose_dimensions} </td>
            <td>
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div
                  className="form-control"
                  onClick={() => !window.user_quote_finalised && setIsDropdownOpen(!isDropdownOpen)}
                  style={{
                    cursor: window.user_quote_finalised ? 'not-allowed' : 'pointer',
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: window.user_quote_finalised ? '#e9ecef' : 'white',
                  }}
                >
                  <span style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    fontSize: '13px',
                  }}>
                    {displayText}
                  </span>
                  <span style={{ marginLeft: '8px', color: '#666' }}>
                    ({layout_dimension_ids.length}/{MAXIMUM_LAYOUT_DIMENSIONS}) ▼
                  </span>
                </div>

                {isDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}>
                    {Object.entries(groupedOptions).map(([groupName, options]) => (
                      <div key={groupName}>
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          color: '#666',
                          borderBottom: '1px solid #eee',
                        }}>
                          {groupName}
                        </div>
                        {options.map((option) => {
                          const isSelected = layout_dimension_ids.includes(option.id);
                          const isDisabled = !isSelected && layout_dimension_ids.length >= MAXIMUM_LAYOUT_DIMENSIONS;

                          return (
                            <label
                              key={option.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 12px',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                opacity: isDisabled ? 0.5 : 1,
                                borderBottom: '1px solid #f0f0f0',
                              }}
                              onClick={(e) => {
                                if (!isDisabled) {
                                  e.preventDefault();
                                  handleCheckboxChange(option.id);
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => {}}
                                style={{ marginRight: '10px' }}
                              />
                              <span style={{ fontSize: '13px' }}>{option.description}</span>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (<>
  <div >

    <input type="hidden" value={layout_dimension_unit} name="layout_dimension_unit"/>
    <table className="table  table-bordered">
      <tbody>
        <tr>
          <td colSpan="2"> &nbsp; </td>
          <td>
            {multiSelect()}
          </td>
        </tr>

        <tr>

          <td>
            { !hideMargins &&
            <OptionRowFloat description={t('layouts.top_margin_diecutting')}
              item={item}
              name="layout_dimension_top_margin_diecutting"
              value={temp_top_margin_diecutting}
              unit={myLayoutContext.unit}
              onChange={handleTopMarginDiecutting}
              disabled={fieldsDisabled}
            /> }
          </td>
          <td>
            { !hideMargins && <OptionRowFloat description={t('layouts.side_margin_diecutting')}
              item={item}
              name="layout_dimension_side_margin_diecutting"
              value={temp_side_margin_diecutting}
              unit={myLayoutContext.unit}
              onChange={handleSideMarginDiecutting}
              disabled={fieldsDisabled}
            /> }
          </td>

          <td>
            { !hideMargins && <OptionRowFloat description={t('layouts.bottom_margin_diecutting')}
              item={item}
              name="layout_dimension_bottom_margin_diecutting"
              value={temp_bottom_margin_diecutting}
              unit={myLayoutContext.unit}
              onChange={handleBottomMarginDiecutting}
              disabled={fieldsDisabled}
            /> }
          </td>

        </tr>

        <tr>
          <td>
            { !hideMargins &&
            <OptionRowFloat description={t('layouts.top_margin_printing')}
              item={item}
              name="layout_dimension_top_margin_printing"
              value={temp_top_margin_printing}
              unit={myLayoutContext.unit}
              onChange={handleTopMarginPrinting}
              disabled={fieldsDisabled}
            /> }
          </td>
          <td>
            { !hideMargins && <OptionRowFloat description={t('layouts.side_margin_printing')}
              item={item}
              name="layout_dimension_side_margin_printing"
              value={temp_side_margin_printing}
              unit={myLayoutContext.unit}
              onChange={handleSideMarginPrinting}
              disabled={fieldsDisabled}
            /> }
          </td>

          <td>
            { !hideMargins && <OptionRowFloat description={t('layouts.bottom_margin_printing')}
              item={item}
              name="layout_dimension_bottom_margin_printing"
              value={temp_bottom_margin_printing}
              unit={myLayoutContext.unit}
              onChange={handleBottomMarginPrinting}
              disabled={fieldsDisabled}
            /> }
          </td>

        </tr>

        { !hideMargins && (
          <tr>
            <td colSpan="3" style={{paddingTop: '0px', paddingBottom: '20px', textAlign: 'right'}}>
              <a
                href="#"
                onClick={handleEditSave}
                style={{
                  pointerEvents: disableMargins ? 'none' : 'auto',
                  opacity: disableMargins ? 0.5 : 1,
                  cursor: disableMargins ? 'not-allowed' : 'pointer',
                  textDecoration: 'none',
                }}
              >
                {!isEditMode && <>  <i className="fa fa-pencil-square-o" aria-hidden="true"></i> Edit margins </>}
                {isEditMode && <> <i className="fa fa-floppy-o" aria-hidden="true"></i> Save margins </>}
              </a>
            </td>
          </tr>
        )}

        <tr>
          <td colSpan="3" style={{paddingTop: '10px', paddingBottom: '10px'}}>
            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={layout_dimension_filtering_enabled}
                onChange={(e) => set_layout_dimension_filtering_enabled(e.target.checked)}
                style={{marginRight: '8px'}}
              />
              <span>Cözüm Filtreleme</span>
            </label>
          </td>
        </tr>

      </tbody>
    </table>

    </div>
    <div style={{'clear': 'both' }} ></div>
  </>)
}

export default ChooseLayoutDimensions;
